import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import axios from 'axios';
import { API_BASE } from '../api';
import { useAuth } from '../context/AuthContext';
import { logEvent } from '../apiUtils';
import '../App.css';

// Status strings from TheSportsDB that indicate a finished game
const FINISHED_STATUSES = new Set(['FT', 'AET', 'PEN', 'FT_PEN', 'MATCH FINISHED']);

// days_primary values from the scraper — how many days before the game it enters the DB
const LEAGUE_DAYS_PRIMARY = { '4387': 3, '4328': 7, '4424': 3, '4346': 6 };

const GamePage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { authData } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pickResult, setPickResult] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [committedPick, setCommittedPick] = useState(''); // locally committed after submission
  const [changingPick, setChangingPick] = useState(false);

  useEffect(() => {
    document.body.classList.add('game-page-bg');
    logEvent('visited_game');
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/sports/game/${gameId}`);
        setData(res.data);

        // Pre-select the user's existing pick if present
        if (authData.isAuthenticated) {
          const myPick = res.data.picks?.find(
            (p) => p.user_id === authData.user?.id
          );
          if (myPick) setSelectedTeam(myPick.picked_team);
        }
      } catch (e) {
        if (e.response?.status === 404) setError('Game not found.');
        else setError('Failed to load game data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    return () => document.body.classList.remove('game-page-bg');
  }, [gameId, authData.isAuthenticated, authData.user?.id]);

  const handleMakePick = async (team) => {
    if (!authData.isAuthenticated) {
      navigate('/login');
      return;
    }
    setSelectedTeam(team);
    setSubmitting(true);
    setPickResult('');
    try {
      const token = localStorage.getItem('token');
      if (changingPick && (committedPick || myPick?.picked_team)) {
        // Update existing pick via dedicated endpoint
        await axios.put(
          `${API_BASE}/user/update-pick/${gameId}`,
          { picked_team: team },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // First-time pick
        await axios.post(
          `${API_BASE}/user/make-pick`,
          [{ game_id: gameId, picked_team: team, sport: data?.fp_game?.sport }],
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setCommittedPick(team);
      setChangingPick(false);
      setPickResult('success');
    } catch (e) {
      setPickResult('error');
      setSelectedTeam('');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
  };

  if (loading) {
    return (
      <BaseLayout>
        <div className="entity-page-container">
          <FlameLoader text="Loading game..." />
        </div>
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout>
        <div className="entity-page-container">
          <p className="error-text">{error}</p>
        </div>
      </BaseLayout>
    );
  }

  if (!data) return null;

  const { event, fp_game, picks } = data;
  const isFinished = event ? FINISHED_STATUSES.has((event.strStatus || '').toUpperCase()) : !!fp_game?.winning_team;
  const isUpcoming = !isFinished && event?.strStatus === 'NS';

  // True if current user already has a pick on this game
  const myPick = picks?.find((p) => p.user_id === authData.user?.id);
  // effectivePick: locally committed pick (optimistic) or server pick — whichever is set
  const effectivePick = committedPick || myPick?.picked_team || '';
  // fp_game must exist (game tracked in FlamePicks DB) before picks are allowed
  // Allow picking if no effective pick yet, OR user is actively changing their pick
  const canPick = authData.isAuthenticated && isUpcoming && !!fp_game && (!effectivePick || changingPick);

  // Game is visible via TheSportsDB but not yet scraped into FlamePicks
  const picksNotYetOpen = !fp_game && isUpcoming;
  const picksOpenDate = (() => {
    if (!picksNotYetOpen || !event?.dateEvent) return null;
    const daysPrimary = LEAGUE_DAYS_PRIMARY[String(event.idLeague)] ?? 3;
    const d = new Date(event.dateEvent + 'T12:00:00');
    d.setDate(d.getDate() - daysPrimary);
    return d;
  })();
  const formatPicksOpenDate = (d) =>
    d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // Determine winning side for score highlighting
  const homeWon =
    isFinished &&
    event?.intHomeScore !== null &&
    event?.intAwayScore !== null &&
    parseInt(event.intHomeScore) > parseInt(event.intAwayScore);
  const awayWon =
    isFinished &&
    event?.intHomeScore !== null &&
    event?.intAwayScore !== null &&
    parseInt(event.intAwayScore) > parseInt(event.intHomeScore);

  // Pick distribution stats
  const isSoccer = event?.strSport === 'Soccer' || fp_game?.sport === 'EPL' || fp_game?.sport === 'MLS';
  const totalPicks = picks?.length || 0;
  const normalize = (s) => (s || '').toLowerCase().trim();
  const homePicks = picks?.filter((p) =>
    normalize(p.picked_team) === normalize(event?.strHomeTeam) ||
    normalize(p.picked_team) === normalize(fp_game?.home_team)
  ).length || 0;
  const awayPicks = picks?.filter((p) =>
    normalize(p.picked_team) === normalize(event?.strAwayTeam) ||
    normalize(p.picked_team) === normalize(fp_game?.away_team)
  ).length || 0;
  const drawPicks = isSoccer ? totalPicks - homePicks - awayPicks : 0;
  const homePct = totalPicks > 0 ? Math.round((homePicks / totalPicks) * 100) : 0;
  const awayPct = totalPicks > 0 ? Math.round((awayPicks / totalPicks) * 100) : 0;
  const drawPct = totalPicks > 0 ? Math.round((drawPicks / totalPicks) * 100) : 0;

  const homeTeam = event?.strHomeTeam || fp_game?.home_team || 'Home';
  const awayTeam = event?.strAwayTeam || fp_game?.away_team || 'Away';
  const homeId   = event?.idHomeTeam;
  const awayId   = event?.idAwayTeam;
  const homeBadge = event?.strHomeTeamBadge;
  const awayBadge = event?.strAwayTeamBadge;

  return (
    <BaseLayout>
      <div className="entity-page-container">

        {/* ── League breadcrumb ── */}
        <div className="game-breadcrumb">
          {event?.idLeague && (
            <Link to={`/league/${event.idLeague}`} className="entity-league-badge">
              {event.strLeague}
            </Link>
          )}
          {event?.strVenue && (
            <span className="entity-meta-tag">{event.strVenue}</span>
          )}
          {event?.dateEvent && (
            <span className="entity-meta-tag">{formatDate(event.dateEvent)}</span>
          )}
        </div>

        {/* ── Matchup hero ── */}
        <div className="game-hero">
          <div
            className={`game-team-panel ${homeWon ? 'game-team-winner' : ''}`}
            onClick={() => homeId && navigate(`/team/${homeId}`)}
            style={{ cursor: homeId ? 'pointer' : 'default' }}
          >
            {homeBadge ? (
              <img src={`${homeBadge}/medium`} alt={homeTeam} className="game-team-logo" />
            ) : (
              <div className="game-team-logo-placeholder" />
            )}
            <span className="game-team-name">{homeTeam}</span>
            <span className="game-team-label">Home</span>
          </div>

          <div className="game-center">
            {isFinished ? (
              <div className="game-score-board">
                <span className={`game-score-num ${homeWon ? 'score-win' : awayWon ? 'score-dim' : ''}`}>
                  {event?.intHomeScore ?? '–'}
                </span>
                <span className="game-score-sep">–</span>
                <span className={`game-score-num ${awayWon ? 'score-win' : homeWon ? 'score-dim' : ''}`}>
                  {event?.intAwayScore ?? '–'}
                </span>
              </div>
            ) : (
              <div className="game-vs-block">
                <span className="game-vs-text">VS</span>
                {event?.strTime && (
                  <span className="game-kickoff-time">{event.strTime} UTC</span>
                )}
              </div>
            )}
            <span className={`game-status-badge ${isFinished ? 'gsb-final' : isUpcoming ? 'gsb-upcoming' : 'gsb-live'}`}>
              {isFinished ? 'Final' : isUpcoming ? 'Upcoming' : event?.strStatus || 'TBD'}
            </span>

            {/* ── Make a Pick buttons ── */}
            {canPick && (
              <div className="game-pick-area">
                <p className="game-pick-label">Make your pick</p>
                <div className="game-pick-btns">
                  <button
                    className={`game-pick-btn ${selectedTeam === homeTeam ? 'game-pick-selected' : ''}`}
                    disabled={submitting}
                    onClick={() => handleMakePick(homeTeam)}
                  >
                    {homeTeam}
                  </button>
                  {/* Draw button only relevant for soccer leagues */}
                  {(event?.strSport === 'Soccer') && (
                    <button
                      className={`game-pick-btn game-pick-draw ${selectedTeam === 'Draw' ? 'game-pick-selected' : ''}`}
                      disabled={submitting}
                      onClick={() => handleMakePick('Draw')}
                    >
                      Draw
                    </button>
                  )}
                  <button
                    className={`game-pick-btn ${selectedTeam === awayTeam ? 'game-pick-selected' : ''}`}
                    disabled={submitting}
                    onClick={() => handleMakePick(awayTeam)}
                  >
                    {awayTeam}
                  </button>
                </div>
                {pickResult === 'success' && (
                  <p className="pick-feedback success">Pick submitted!</p>
                )}
                {pickResult === 'error' && (
                  <p className="pick-feedback error">Failed to submit pick. Try again.</p>
                )}
              </div>
            )}

            {picksNotYetOpen && (
              <div className="game-pick-area game-picks-locked">
                <p className="game-pick-label">Make your pick</p>
                <div className="game-pick-btns">
                  <button className="game-pick-btn" disabled>{homeTeam}</button>
                  {event?.strSport === 'Soccer' && (
                    <button className="game-pick-btn game-pick-draw" disabled>Draw</button>
                  )}
                  <button className="game-pick-btn" disabled>{awayTeam}</button>
                </div>
                <p className="game-picks-locked-msg">
                  Picks will open for this game at <strong>2:00 AM EST</strong>
                  {picksOpenDate && (
                    <> on <strong>{formatPicksOpenDate(picksOpenDate)}</strong></>
                  )}
                </p>
              </div>
            )}

            {effectivePick && !changingPick && isUpcoming && (
              <div className="game-pick-area">
                <p className="game-pick-label">Your pick</p>
                <span className="game-my-pick-badge">{effectivePick}</span>
                <button
                  className="game-change-pick-btn"
                  onClick={() => { setChangingPick(true); setPickResult(''); setSelectedTeam(effectivePick); }}
                >
                  Change Pick
                </button>
              </div>
            )}

            {myPick && isFinished && (
              <div className="game-pick-area">
                <p className="game-pick-label">Your pick</p>
                <span className={`game-my-pick-badge ${myPick.result === 1 ? 'pick-win' : myPick.result === 0 ? 'pick-loss' : ''}`}>
                  {myPick.picked_team}
                  {myPick.result === 1 && ' ✓'}
                  {myPick.result === 0 && ' ✗'}
                </span>
              </div>
            )}
          </div>

          <div
            className={`game-team-panel ${awayWon ? 'game-team-winner' : ''}`}
            onClick={() => awayId && navigate(`/team/${awayId}`)}
            style={{ cursor: awayId ? 'pointer' : 'default' }}
          >
            {awayBadge ? (
              <img src={`${awayBadge}/medium`} alt={awayTeam} className="game-team-logo" />
            ) : (
              <div className="game-team-logo-placeholder" />
            )}
            <span className="game-team-name">{awayTeam}</span>
            <span className="game-team-label">Away</span>
          </div>
        </div>

        {/* ── Pick Distribution Bar ── */}
        {totalPicks > 0 && (
          <section className="entity-section">
            <h2 className="section-title">Community Picks — {totalPicks} total</h2>
            <div className="pick-dist-bar-wrap">
              <div className="pick-dist-labels">
                <span>{homeTeam} <strong>{homePct}%</strong></span>
                {drawPicks > 0 && <span>Draw <strong>{drawPct}%</strong></span>}
                <span>{awayTeam} <strong>{awayPct}%</strong></span>
              </div>
              <div className="pick-dist-bar">
                {homePct > 0 && (
                  <div className="pick-bar-home" style={{ width: `${homePct}%` }} title={`${homeTeam}: ${homePct}%`} />
                )}
                {drawPct > 0 && (
                  <div className="pick-bar-draw" style={{ width: `${drawPct}%` }} title={`Draw: ${drawPct}%`} />
                )}
                {awayPct > 0 && (
                  <div className="pick-bar-away" style={{ width: `${awayPct}%` }} title={`${awayTeam}: ${awayPct}%`} />
                )}
              </div>
            </div>
          </section>
        )}

        {/* ── Picks List ── */}
        {picks?.length > 0 && (
          <section className="entity-section">
            <h2 className="section-title">Who Picked What</h2>
            <div className="pickers-table-wrap">
              <table className="pickers-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Pick</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {picks.map((p) => (
                    <tr
                      key={p.user_id}
                      className="picker-row clickable"
                      onClick={() => navigate(`/user/${p.username}`)}
                    >
                      <td className="username-col">{p.username}</td>
                      <td>{p.picked_team}</td>
                      <td>
                        {p.result === 1 && <span className="win-text">Win</span>}
                        {p.result === 0 && <span className="loss-text">Loss</span>}
                        {p.result === null && <span className="pending-text">Pending</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

      </div>
    </BaseLayout>
  );
};

export default GamePage;
